// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useMutation } from '@apollo/client';
import { TASK_STATUS, formatTime, isPlantStep, utils } from '@seedplanner/shared-utils';
import { enqueueSnackbar } from 'notistack';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { useGating, useUserContext } from '../../context/UserContext';
import { Chip, IconButton, Typography } from '../../lib/base';
import { Box } from '../../lib/base';
import { CheckCircleIcon, CheckCircleOutlineIcon } from '../../lib/icons';
import { DASHBOARD_TASKS_COMPLETED_TODAY_QUERY } from '../../queries/DashboardQueries';
import { EDIT_TASK_MUTATION } from '../../queries/EditTaskQueries';
import type {
  ChangeTaskGroupStatusMutation,
  ChangeTaskGroupStatusMutationVariables,
} from '../../types/__generated__/graphql';
import { compareTasks, getID, getNumericID, getTimeRanges } from '../../utils';
import { VisibleAtContainer } from '../common/containers/VisibleAtContainer';
import { StackedTitle, Table } from '../common/table';
import type { ColumnType } from '../common/table/Table';
import { Time } from '../common/time';
import { CropIdLink } from '../crop';
import { PlantingGroupIdLink } from '../plantingGroup';
import TaskGroupIdLink from './TaskGroupIdLink';

export interface TaskData {
  readonly id: string;
  readonly task_name: string;
  readonly task_status: string;
  readonly task_time: number;
  readonly task_completed_by_name?: string | null;
  readonly planting?: PlantingDataBase | null;
  readonly crop?: {
    readonly id: string;
    readonly crop_name: string;
  } | null;
}
interface PlantingDataBase {
  readonly id: string;
  readonly order_id?: string | null;
  readonly trays_rounded: string;
  readonly planted_at?: number | null;
  readonly planting_time?: number; // For now only show harvest time/link if planting time is defined (and showHarvestTimeColumn is true)
}
interface Props {
  readonly taskGroups: TaskData[][];
  readonly showActionButtons: boolean;
  readonly showTaskCompletedByColumn?: boolean;
  readonly showPlantingTimeColumn?: boolean;
  readonly showHarvestTimeColumn?: boolean;
  readonly onCompleted?: () => void;
}

function MarkCompleteButton({
  isComplete,
  tasks,
  onCompleted,
}: { readonly isComplete: boolean; readonly tasks: readonly TaskData[]; readonly onCompleted?: () => void }) {
  const { timezone } = useUserContext();
  const { canUseBasic } = useGating();
  const { todayStart, todayEnd } = getTimeRanges(timezone);
  const [markComplete, { loading }] = useMutation<
    ChangeTaskGroupStatusMutation,
    ChangeTaskGroupStatusMutationVariables
  >(EDIT_TASK_MUTATION);

  const task = tasks[0];
  const isPlantTask = isPlantStep(task.task_name);

  return (
    <IconButton
      aria-label="mark-task-group-complete"
      id="complete-tasks"
      disabled={loading || !canUseBasic || isComplete}
      onClick={() =>
        void markComplete({
          refetchQueries: [
            {
              query: DASHBOARD_TASKS_COMPLETED_TODAY_QUERY,
              variables: {
                todayStart: todayStart.toString(),
                todayEnd: todayEnd.toString(),
              },
            },
          ],
          variables: {
            ids: tasks.map((task) => getNumericID(task.id)),
          },
          onCompleted: () => {
            if (isPlantTask && onCompleted) {
              onCompleted();
            }
            enqueueSnackbar('Completed task', { variant: 'success' });
          },
          update: (cache, { data }) => {
            const plantingsCreated = (data?.changeTaskStatuses?.plantings_created?.length ?? 0) > 0;
            const tasksCreated = (data?.changeTaskStatuses?.tasks_created?.length ?? 0) > 0;
            cache.modify({
              fields: {
                harvests(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                plantings(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                taskGroup(existingGroup, { DELETE }) {
                  return tasksCreated ? DELETE : existingGroup;
                },
                plantingGroup(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                planting_groups(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                plantingTimeGroup(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
              },
            });
          },
          onError: (err) => {
            enqueueSnackbar(err.message, {
              variant: 'error',
              autoHideDuration: 10000,
            });
          },
        })
      }
    >
      {isComplete ? <CheckCircleIcon /> : <CheckCircleOutlineIcon />}
    </IconButton>
  );
}

export default function TaskGroupTable({
  taskGroups,
  showActionButtons,
  showTaskCompletedByColumn = false,
  showPlantingTimeColumn = false,
  showHarvestTimeColumn = false,
  onCompleted,
}: Props) {
  const { timezone, permissions } = useUserContext();
  const userCanCompleteTasks = permissions.can_complete_tasks;
  const showCompleteColumn = showActionButtons && userCanCompleteTasks;

  const { todayStart, todayEnd } = getTimeRanges(timezone);
  const isToday = (time: number) => time >= todayStart && time <= todayEnd;
  const isOverdue = (time: number) => time < todayStart;

  const groupedTaskLinks: React.ReactElement[] = [];
  const totalTrayCounts: string[] = [];
  const cropLinkValues: (React.ReactElement | string)[] = [];
  const timeValues: React.ReactElement[] = [];
  const harvestLinkValues: (React.ReactElement | string)[] = [];
  const plantedAtValues: string[] = [];
  const plantingCounts: number[] = [];
  const orderCounts: number[] = [];
  const markAsCompleteButtons: React.ReactElement[] = [];
  const taskCompletedByNames: React.ReactElement[] = [];

  const taskGroupsCopy = taskGroups
    .filter(utils.notNull)
    .filter((group) => Array.isArray(group) && group.length > 0)
    .map((group) => [...group].filter(utils.notNull));
  taskGroupsCopy.sort((a, b) => {
    const taskA = a[0];
    const taskB = b[0];
    if (taskA == null || taskB == null) {
      throw new Error('Something went wrong with task groups. Found a null task when sorting task groups');
    }

    return compareTasks(taskA, taskB);
  });

  // Fail-safe to prevent harvest link from showing if there is no planting time
  let harvestLinkValuesPushed = false;

  for (const tasks of taskGroupsCopy) {
    const isComplete = tasks.every((task) => task.task_status === TASK_STATUS.COMPLETED);

    markAsCompleteButtons.push(
      <Box
        sx={{
          py: {
            xs: 1.25,
            sm: 0,
            '@xs': 1.25,
            '@sm': 0,
          },
        }}
      >
        <MarkCompleteButton isComplete={isComplete} tasks={tasks} onCompleted={onCompleted} />
      </Box>,
    );

    const sampleTask = tasks[0];
    const totalTrays = Math.ceil(
      tasks.reduce(
        (acc, task) =>
          utils.addTraysRaw(
            acc,
            task.planting?.trays_rounded !== undefined ? Number.parseFloat(task.planting.trays_rounded) : 0,
          ),
        0,
      ),
    );
    totalTrayCounts.push(totalTrays === 0 ? '' : totalTrays.toString());
    const smallTaskGroupLink = (
      <StackedTitle
        title={
          <TaskGroupIdLink
            id={getID(sampleTask.id)}
            title={`${sampleTask.task_name}${sampleTask.crop?.crop_name ? ` - ${sampleTask.crop.crop_name}` : ''}`}
          />
        }
        subtitle={`${formatTime(sampleTask.task_time, { timezone })}${
          totalTrays > 0 ? ` - ${totalTrays} ${totalTrays > 1 ? 'trays' : 'tray'}` : ''
        }`}
      />
    );
    const normalTaskGroupLink = <TaskGroupIdLink id={getID(sampleTask.id)} title={sampleTask.task_name} />;

    groupedTaskLinks.push(<VisibleAtContainer up={normalTaskGroupLink} down={smallTaskGroupLink} />);

    cropLinkValues.push(
      sampleTask.crop ? <CropIdLink id={getID(sampleTask.crop.id)} name={sampleTask.crop.crop_name} /> : '',
    );

    timeValues.push(
      <Chip
        size="small"
        color={
          sampleTask.task_status === TASK_STATUS.COMPLETED
            ? 'default'
            : isToday(sampleTask.task_time)
              ? 'warning'
              : isOverdue(sampleTask.task_time)
                ? 'error'
                : 'default'
        }
        label={
          <VisibleAtContainer
            up={<Time time={sampleTask.task_time} options={{ compactDisplay: false }} />}
            down={<Time time={sampleTask.task_time} options={{ compactDisplay: true }} />}
          />
        }
      />,
    );

    const allHarvestTimes = tasks.map((t) => t.planting?.planting_time).filter(utils.notNull);
    const hasMultipleHarvestTimes = new Set(allHarvestTimes).size > 1;
    harvestLinkValues.push(
      sampleTask.planting != null && sampleTask.planting.planting_time != null ? (
        hasMultipleHarvestTimes ? (
          <TaskGroupIdLink id={getID(sampleTask.id)} title="Multiple" />
        ) : (
          <PlantingGroupIdLink
            id={getID(sampleTask.planting.id)}
            title={formatTime(sampleTask.planting.planting_time, { timezone, removeDayOfWeek: true })}
          />
        )
      ) : (
        '-'
      ),
    );
    const allPlantedAtDates = new Set<string>(
      tasks
        .map((t) => t.planting?.planted_at)
        .map((p) => (p == null ? 'none' : formatTime(p, { timezone, removeDayOfWeek: true }))),
    );
    const hasMultiplePlantedAtDates = allPlantedAtDates.size > 1;
    plantedAtValues.push(
      hasMultiplePlantedAtDates
        ? 'Multiple'
        : sampleTask.planting != null && sampleTask.planting.planted_at != null
          ? formatTime(sampleTask.planting.planted_at, { timezone, removeDayOfWeek: true })
          : '-',
    );
    if (sampleTask.planting != null && sampleTask.planting.planting_time !== undefined) {
      harvestLinkValuesPushed = true;
    }

    const plantingCount = new Set(tasks.map((task) => task.planting?.id).filter(utils.notNull)).size;
    plantingCounts.push(plantingCount);

    const orderCount = new Set(tasks.map((task) => task.planting?.order_id).filter(utils.notNull)).size;
    orderCounts.push(orderCount);

    const taskCompletedByNameMap = new Map<string, number>();
    for (const task of tasks) {
      if (task.task_completed_by_name != null) {
        const maybeExistingCount = taskCompletedByNameMap.get(task.task_completed_by_name);
        if (maybeExistingCount == null) {
          taskCompletedByNameMap.set(task.task_completed_by_name, 1);
        } else {
          taskCompletedByNameMap.set(task.task_completed_by_name, maybeExistingCount + 1);
        }
      }
    }
    let taskCompletedByName = '';
    let taskCompletedByNameCount = 0;
    // Get name with most completed tasks
    for (const [name, count] of taskCompletedByNameMap) {
      if (count > taskCompletedByNameCount) {
        taskCompletedByName = name;
        taskCompletedByNameCount = count;
      }
    }

    let firstNameOnly = taskCompletedByName;
    const splitName = taskCompletedByName.split(' ');
    if (splitName.length > 1) {
      firstNameOnly = splitName[0];
    }

    // This can overflow easily if the name is too long
    const smallCompletedByName = (
      <Box
        sx={{
          height: '60px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}
      >
        <Typography>{firstNameOnly}</Typography>
      </Box>
    );

    taskCompletedByNames.push(
      <VisibleAtContainer down={smallCompletedByName} up={<Typography>{firstNameOnly}</Typography>} />,
    );
  }
  const columns: ColumnType[] = [
    {
      name: 'Name',
      smallName: 'Task',
      values: groupedTaskLinks,
    },
    {
      name: 'Trays',
      values: totalTrayCounts,
      numeric: true,
      visibleAt: 'sm',
    },
    {
      name: 'Crop',
      values: cropLinkValues,
      visibleAt: 'sm',
    },
    {
      name: 'Date',
      values: timeValues,
      visibleAt: 'sm',
    },
  ];
  if (showPlantingTimeColumn) {
    columns.push({
      name: 'Planted On',
      values: plantedAtValues,
      visibleAt: 'xl',
    });
  }
  if (showHarvestTimeColumn && harvestLinkValuesPushed) {
    columns.push({
      name: 'For Harvest',
      values: harvestLinkValues,
      visibleAt: 'lg',
    });
  }
  if (showTaskCompletedByColumn) {
    columns.push({
      name: 'Completed By',
      values: taskCompletedByNames,
      wrapOverFlow: false,
    });
  }
  // Be careful not showing complete action column on mobile/small screens
  if (showCompleteColumn) {
    columns.push({
      name: 'Complete',
      smallName: '',
      values: markAsCompleteButtons,
      numeric: true,
    });
  }

  return <Table columns={columns} />;
}
